// ═══════════════════════════════════════════════════════════════════════
// Live-hero clamp + freeze (B2) — pure, tested. Extracted from
// CrashMultiplierDisplay's rAF loop so the "never overshoot M / never
// visibly correct downward" invariant can be asserted without React.
//
// THE BUG this closes (owner report): the hero climbed to 2.43x, then the
// race_ended packet arrived with M = 2.12x and the number snapped DOWN to
// 2.12x — a visible downward correction. It happened because, BEFORE
// race_ended lands in the event list, the live branch had no ceiling: it
// showed multiplierFor(tDisplay) raw, and tDisplay (a fresh beacon
// extrapolated by +age, or the wall-clock event fallback) can run PAST the
// real crash frame. So the hero overshot the crash point the backend then
// settled at.
//
// Fix — three guards, all here:
//   F1  the shown value is UNCONDITIONALLY min(base, mMax): once the crash
//       point is known (from the event OR the latched stamp) the hero can
//       never exceed it, so there is nothing to correct downward later.
//   F2  the ceiling survives event-list churn: if race_ended drops out of
//       the list the LATCHED crash multiplier still caps the hero (no
//       re-climb from the wall clock).
//   F3  the event-clock fallback is bounded: a stale beacon whose wall-clock
//       tDisplay has run past the crash moment holds at t_crash (→ mMax)
//       instead of climbing past it.
//
// The crash-curve contract (CRASH_RATE, reveal delay) lives in the leaf
// ./crashCurve module (single source of truth) shared by the engine / Go
// backend / this widget — it MUST stay identical. Re-exported here so
// existing importers of these symbols from crashHeroClamp keep working.
// ═══════════════════════════════════════════════════════════════════════

export { CRASH_RATE, CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'
import { CRASH_RATE, CRASH_REVEAL_DELAY_SECONDS } from './crashCurve'
import { BEACON_DEAD_MS } from './crashFrameBeacon'

/** The climb curve: flat 1.00x through the reveal hold, then e^(rate·climb). */
export function multiplierForElapsed(tDisplay: number): number {
  const elapsed = Math.max(0, tDisplay - CRASH_REVEAL_DELAY_SECONDS)
  return Math.round(Math.exp(CRASH_RATE * elapsed) * 100) / 100
}

/**
 * Race-relative time at which the crash frame occurs for a settled M:
 * the reveal hold plus the climb time ln(M)/rate. Mirrors useVisualCrash's
 * crashTimeFor so the display and the crash-hold decision agree on t_crash.
 */
export function crashTimeForMultiplier(m: number): number {
  return CRASH_REVEAL_DELAY_SECONDS + Math.log(m) / CRASH_RATE
}

/**
 * The ceiling the backend will settle at, as known on THIS frame:
 *   - the crash event's M when it is in the list (authoritative), else
 *   - the latched M once the crash has stamped (F2 — survives churn), else
 *   - undefined: not known yet, no ceiling.
 * A non-finite / sub-1 value is treated as unknown (never a valid ceiling).
 */
export function resolveHeroCeiling(
  crashedMultiplier: number | undefined,
  latchedMultiplier: number | null
): number | undefined {
  const valid = (m: number | null | undefined): m is number =>
    typeof m === 'number' && Number.isFinite(m) && m >= 1
  if (valid(crashedMultiplier)) return crashedMultiplier
  if (valid(latchedMultiplier)) return latchedMultiplier
  return undefined
}

/**
 * F3 — bound the display clock so a stale beacon can't climb the hero past
 * the crash. When the beacon is fresh we trust tDisplay as-is (it IS the
 * viewer's frame clock). When it is stale AND the crash point is known, the
 * event/wall clock may have run past t_crash; cap tDisplay at t_crash so the
 * curve holds at mMax instead of overshooting.
 */
export function boundDisplayTime(
  tDisplay: number,
  beaconFresh: boolean,
  ceiling: number | undefined
): number {
  if (beaconFresh || ceiling === undefined) return tDisplay
  return Math.min(tDisplay, crashTimeForMultiplier(ceiling))
}

/**
 * The hero display clock (race-time seconds) for this frame.
 *
 * Priority: the frame beacon (accurate to the exact frame on screen) over the
 * event clock. When the beacon is FRESH we extrapolate from it between its 8Hz
 * updates: `beacon.t + ageMs/1000`.
 *
 * When the beacon goes briefly STALE (video stalled) we keep reading from the
 * SAME beacon origin, capped at `freshMs`, instead of snapping to the event
 * clock. The event clock leads the frames by the full stream sync-delta
 * (~1.6–2.3s), so on the stale transition it would jerk the hero ~2s forward
 * over a frozen video. Holding from the beacon keeps the number where the last
 * frame put it; the moment the beacon re-locks it corrects. (Hidden-tab time
 * does NOT count toward staleness — useFrameBeacon shifts the lock's age by
 * the hidden span on restore.)
 *
 * We fall back to the event clock in exactly two cases: there was NEVER a
 * beacon this round (`beacon === null`), or the beacon is DEAD past
 * BEACON_DEAD_MS. Holding a dead beacon forever pinned the clock for the rest
 * of the round (incident 2026-08-17).
 */
export function resolveDisplayTime(
  beacon: { t: number; ageMs: number } | null,
  freshMs: number,
  eventElapsed: number | undefined
): number | undefined {
  // Event clock wins when there is no beacon worth trusting: none this round,
  // or one DEAD past BEACON_DEAD_MS (a decoder that stops mid-round leaves a
  // non-null reading of unbounded age — holding it pinned the clock for the
  // rest of the round, incident 2026-08-17; see admitDecodedT's docblock).
  // Through betting/pre-roll (no event clock yet) holding still beats blank.
  if (beacon === null || (beacon.ageMs >= BEACON_DEAD_MS && eventElapsed !== undefined)) {
    return eventElapsed
  }
  return beacon.t + Math.min(beacon.ageMs, freshMs) / 1000
}

/**
 * F1 (+F2/F3 composed) — the value the live hero must show this frame.
 * Never exceeds the known crash point, so the stamp lands on M with no
 * visible downward correction (Aviator-style freeze).
 *
 * F4 (stamp-flash fix): when a crash event is KNOWN but carries no usable
 * multiplier (payload-less race_crashed — the M is unrevealed), the hero
 * FREEZES at the last value the viewer saw. It may neither keep climbing
 * on the clock (would overshoot the real M the backend settles) nor reset
 * toward 1.00x (the stamp-flash bug). `shownMax` is the running max the
 * hero displayed this round — ≈ the value on screen, since the clamped
 * climb is monotonic.
 */
export function resolveLiveHeroMultiplier(input: {
  tDisplay: number
  beaconFresh: boolean
  crashedMultiplier: number | undefined
  latchedMultiplier: number | null
  // F4 — a race_crashed/race_ended event exists (the round is over) even
  // if no usable M was found. Optional so pre-F4 callers/tests keep their
  // exact contract.
  crashKnown?: boolean
  shownMax?: number
  // Review 2026-08-17: with the beacon DEAD the display clock rides the event
  // clock, which is past t_crash once the crash is known — capping at t_crash
  // parked the hero at exactly M over footage that had not crashed yet,
  // leaking through the number the value the stamp grace conceals. Dead +
  // crashKnown freezes at the last shown value instead (never above M).
  beaconDead?: boolean
}): number {
  const ceiling = resolveHeroCeiling(input.crashedMultiplier, input.latchedMultiplier)
  if (input.beaconDead && input.crashKnown) {
    const frozen = frozenHeroValue(input.shownMax ?? 1)
    return ceiling === undefined ? frozen : Math.min(frozen, ceiling)
  }
  if (ceiling === undefined && input.crashKnown) return frozenHeroValue(input.shownMax ?? 1)
  const boundedT = boundDisplayTime(input.tDisplay, input.beaconFresh, ceiling)
  const base = multiplierForElapsed(boundedT)
  return ceiling === undefined ? base : Math.min(base, ceiling)
}

/**
 * The hero value while the crash is known but M is not: the last value the
 * viewer saw, floored at the curve's 1.00x origin. A pre-climb crash
 * legitimately freezes (and later stamps) 1.00x — that IS the crash point.
 */
export function frozenHeroValue(shownMax: number): number {
  return Math.max(1, shownMax)
}

/**
 * F5 (v47) — the monotonic RENDER guard: the live hero never steps below
 * the running max it already showed this round.
 *
 * THE CASE this closes: a viewer joining/refreshing MID-ROUND has no
 * beacon until the stream connects, so the hero rides the event clock —
 * which leads the frames by the ~1.6-2.3s sync delta. The first beacon
 * lock steps tDisplay back by that delta and the number visibly dropped
 * (8.9x → 8.3x), the same downward-correction class B2 eliminated at the
 * stamp. Holding at the shown max instead makes the number PAUSE for
 * ≈ the sync delta while the frame-true curve catches up — the exponential
 * closes the gap in roughly the delta itself — then resume climbing.
 * During normal fresh-beacon operation the curve is monotonic, so this
 * guard is a no-op.
 */
export function holdMonotonicLiveValue(candidate: number, shownMax: number): number {
  return Math.max(candidate, shownMax)
}

// How long the hero may hold the frozen live look while a crash is known
// but its multiplier is unrevealed, before force-stamping the frozen value
// so the UI can never hang un-stamped (race_ended normally lands well
// within a second of the broadcast). Mirrors useVisualCrash's
// CRASH_HOLD_GRACE_MS beacon-return grace.
export const CRASH_STAMP_NO_M_GRACE_MS = BEACON_DEAD_MS

export type HeroStampDecision =
  | Readonly<{
      /** Park on the red CRASHED stamp, frozen at `multiplier` — final. */
      stamp: true
      multiplier: number
      /** Grace bookkeeping the caller carries into its next call. */
      noMSinceMs: number | null
    }>
  | Readonly<{
      /** Keep the live look (frozen at `holdMultiplier`) — M not known yet. */
      stamp: false
      holdMultiplier: number
      noMSinceMs: number
    }>

/**
 * The stamp commitment (stamp-flash fix) — called once resolveCrashHold has
 * said the crash visuals may land. The hero's stamped NUMBER is final the
 * moment it is shown, so it may only commit when a trustworthy multiplier
 * exists:
 *   - the crash event's M (authoritative) or the latched M (F2, event
 *     churned out) → stamp it. This is the whole normal path.
 *   - NO usable M anywhere (payload-less crash event) → do NOT stamp —
 *     hold the live look frozen at the last shown value (never the 1.00x
 *     floor the old `?? 1` fallback rendered) and wait for race_ended's M,
 *     force-stamping the frozen value only after CRASH_STAMP_NO_M_GRACE_MS
 *     so the UI can never hang live forever.
 */
export function resolveHeroStamp(input: {
  crashedMultiplier: number | undefined
  latchedMultiplier: number | null
  shownMax: number
  nowMs: number
  noMSinceMs: number | null
  graceMs?: number
}): HeroStampDecision {
  const known = resolveHeroCeiling(input.crashedMultiplier, input.latchedMultiplier)
  if (known !== undefined) return { stamp: true, multiplier: known, noMSinceMs: null }

  const frozen = frozenHeroValue(input.shownMax)
  const since = input.noMSinceMs ?? input.nowMs
  if (input.nowMs - since >= (input.graceMs ?? CRASH_STAMP_NO_M_GRACE_MS)) {
    return { stamp: true, multiplier: frozen, noMSinceMs: since }
  }
  return { stamp: false, holdMultiplier: frozen, noMSinceMs: since }
}

/**
 * Telemetry gate (one-shot): fire when race_ended arrives and the last value
 * the viewer actually saw exceeded the settled M by more than this — i.e. a
 * visible downward correction slipped through. `shownMax` is the running max
 * the hero displayed this round.
 */
export const HERO_OVERSHOOT_LOG_THRESHOLD = 0.05

export function heroOvershoot(shownMax: number, m: number): number {
  return shownMax - m
}

export function shouldLogHeroOvershoot(shownMax: number, m: number): boolean {
  if (!Number.isFinite(m) || m < 1) return false
  return heroOvershoot(shownMax, m) > HERO_OVERSHOOT_LOG_THRESHOLD
}
